import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ReactSortable } from "react-sortablejs";
import { isMobile } from "react-device-detect";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import DeleteCategory from "features/menus/components/modals/DeleteCategory";
import { Accordion } from "features/menu/components/AccordionN";

import SingleCategory from "features/menus/components/SingleCategory";
import CategoryItem from "features/menus/components/CategoryItem";

import useLinkUnlinkStoreWithMenu from "../hooks/useLinkUnlinkStoreWithMenu";
import useAllStores from "hooks/useCurrentUserStores";

import { UTILS } from "utils";
import { useSortCategoryInMenuMutation } from "redux/menu/service";

import { errorHandler } from "api/api/errorHandler";
import OrderingPagePreview from "../components/OrderingPagePreview";
import LinkStoresWithMenu from "../components/modals/LinkStoresWithMenu";
import useGetMenuInfo from "../hooks/useGetMenuInfo";
import { useMenuContext } from "store/contexts/Menus";
import MakeDefaultMenuModal from "features/menus/components/modals/MakeDefaultMenuModal";
import { LoaderAction } from "redux/actions";
import { useGetMenuCatInfoMutation } from "redux/menu/service";
import AddEditCategory from "../components/add-edit-category";
import AppContainer from "components/ui/AppContainer";
import AppButton from "components/ui/AppButton";
import AppOutlineButton from "components/ui/AppOutlineButton";

const MenuEditor = ({ as = "page", menuId, setCurrentMode }) => {
  const params = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { stores, setLoading, loading } = useAllStores();
  const { menuState, updateState } = useMenuContext();
  const { isLoading } = useSelector((state) => state.LoaderReducer);

  const getGetMenuInfoByMenuId = useGetMenuInfo({
    setLoading,
    updateState,
  });

  //mutations
  const [sortCategoryInMenu] = useSortCategoryInMenuMutation();
  //hooks

  const DG_CONTENT_ID = {
    ADD_CATEGORY: 0,
    DELETE_CATEGORY: 1,
    UPDATE_CATEGORY: 2,
    ADD_ITEM_TO_CATEGORY: 3,
  };

  //ADD_CATEGORY
  const handleAddCategory = (cat) => {
    let arr = [...menuState.categories];
    arr.push(cat);
    updateState("categories", arr);
    updateState("open", false);
  };

  //DELETE_CATEGORY
  const handleDeleteCategory = (index) => {
    let arr = [...menuState.categories];
    arr.splice(index, 1);
    updateState("categories", arr);
    updateState("open", false);
  };

  //UPDATE_CATEGORY
  const handleUpdateCategory = (cat) => {
    const arr = [...menuState.categories];
    let updated = {
      categoryId: {
        ...cat,
      },
    };
    arr[menuState.indexToUpdateDelete] = updated;
    updateState("categories", arr);
    updateState("open", false);
  };

  const onClose = () => updateState("open", false);

  useEffect(() => {
    if (params?.menuId || menuId) {
      updateState("menuId", params?.menuId || menuId);
      getGetMenuInfoByMenuId(params?.menuId || menuId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.menuId, menuId]);

  //Fetch  all brand items

  const unLinkMenuCat = (index) => {
    const cat = menuState.categories[index];
    updateState("dialogContentId", DG_CONTENT_ID.DELETE_CATEGORY);
    updateState("categoryToUpdateDelete", cat);
    updateState("indexToUpdateDelete", index);
    updateState("open", true);
  };
  const [items, setItems] = useState([]);
  const [addItemTrigger, setAddItemTrigger] = useState({
    categoryId: null,
    nonce: 0,
  });
  const [getMenuCatInfo] = useGetMenuCatInfoMutation();

  const getCategoryInfo = useCallback(
    async (categoryId, options = {}) => {
      const { silent = false } = options;
      try {
        if (!categoryId) return;
        if (!silent) {
          setLoading(true);
          dispatch(LoaderAction.setLoading(true));
        }
        const userDetail = UTILS.getCurrentUser();
        const data = await getMenuCatInfo({
          brandId: userDetail.brandId,
          categoryId,
          menuId: params?.menuId || menuId,
        });
        if (
          data?.data?.success === 1 &&
          Array.isArray(data?.data?.data?.items)
        ) {
          setItems(data.data.data.items);
        } else {
          UTILS.errorNotification(errorHandler(data?.error?.data));
        }
      } catch (error) {
        UTILS.errorNotification(errorHandler(error?.message));
      } finally {
        if (!silent) {
          setLoading(false);
          dispatch(LoaderAction.setLoading(false));
        }
      }
    },
    [dispatch, getMenuCatInfo, params?.menuId, menuId, setLoading]
  );

  const getCategoryView = (cat, index) => {
    return (
      <div key={cat?._id}>
        <SingleCategory
          {...{ unLinkMenuCat, editCat, cat }}
          toggle={cat?._id}
          keyVal={index}
          getCategoryInfo={getCategoryInfo}
          onAddItem={(categoryId) => {
            setAddItemTrigger((prev) => ({
              categoryId,
              nonce: prev.nonce + 1,
            }));
          }}
        />
        <CategoryItem
          {...{
            setLoading,
            cat,
            connectedStores: menuState.itemLinkedStores,
            loading,
            items,
            setItems,
          }}
          addItemTrigger={addItemTrigger}
          getCategoryInfo={getCategoryInfo}
          menuId={params?.menuId}
          brandItems={menuState?.items}
        />
      </div>
    );
  };

  const renderModalContent = () => {
    switch (menuState.dialogContentId) {
      case 0:
        return (
          <AddEditCategory
            open={menuState?.open}
            mode="add"
            handleValues={handleAddCategory}
            onClose={onClose}
            menuId={params?.menuId}
          />
        );
      case 1:
        return (
          <DeleteCategory
            open={menuState?.open}
            handleValues={handleDeleteCategory}
            onClose={onClose}
            cat={menuState.categoryToUpdateDelete}
            index={menuState.indexToUpdateDelete}
          />
        );
      case 2:
        return (
          <AddEditCategory
            open={menuState?.open}
            mode="edit"
            handleValues={handleUpdateCategory}
            onClose={onClose}
            cat={menuState.categoryToUpdateDelete.categoryId}
            index={menuState.indexToUpdateDelete}
          />
        );
      default:
        break;
    }
  };

  const editCat = (index) => {
    const cat = menuState.categories[index];
    updateState("dialogContentId", DG_CONTENT_ID.UPDATE_CATEGORY);
    updateState("categoryToUpdateDelete", cat);
    updateState("indexToUpdateDelete", index);
    updateState("open", true);
  };

  const reOrderCategory = async (cat, sortable, evt) => {
    try {
      const userDetail = UTILS.getCurrentUser();
      setLoading(true);
      dispatch(LoaderAction.setLoading(true));
      const result = await sortCategoryInMenu({
        brandId: userDetail.brandId,
        menuId: menuState?.menuId || params?.menuId || menuId,
        categoryId: cat.item.dataset.id,
        index: cat.newIndex,
      });
      if (result?.data?.success === 1) {
      } else UTILS.errorNotification(errorHandler(result?.error?.data));
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.message));
    } finally {
      setLoading(false);
      dispatch(LoaderAction.setLoading(false));
    }
  };

  const { linkMenu } = useLinkUnlinkStoreWithMenu({
    setLoading,
    updateState,
    selectedStores: menuState?.linkedStores,
    getAllMenus: () => getGetMenuInfoByMenuId(params?.menuId || menuId),
    menuId: params?.menuId || menuId,
  });

  const [isMakeDefaultOpen, setIsMakeDefault] = useState(false);

  return (
    <AppContainer
      wrapperProps={{ style: { overflow: "hidden" } }}
      containerProps={{
        className: "relative md:p-0 md:pe-5",
        // style: { overflow: "hidden" },
      }}
      showBackButton={as !== "page"}
      backButtonText={t("Menus")}
      backButtonPath={as !== "page" ? () => setCurrentMode("list") : null}
      title={t("Menu Editor")}
      loading={isLoading}
    >
      <MakeDefaultMenuModal
        {...{ isLoading, setIsMakeDefault, isMakeDefaultOpen }}
        menuId={params?.menuId || menuId}
      />

      <LinkStoresWithMenu
        {...{
          updateState,
          stores,
          loading,
          linkMenu,
        }}
        menuState={menuState}
      />

      {(menuState?.dialogContentId === DG_CONTENT_ID.ADD_CATEGORY ||
        menuState?.dialogContentId === DG_CONTENT_ID.UPDATE_CATEGORY ||
        menuState?.dialogContentId === DG_CONTENT_ID.DELETE_CATEGORY) &&
        renderModalContent()}

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 md:grid-cols-3 md:items-stretch md:h-full">
        <div className="relative min-h-0 min-w-0 md:col-span-2 md:h-full md:overflow-y-auto md:pe-3">
          <div
            className="sticky top-0  bg-white flex justify-between gap-4 py-5"
            style={{ zIndex: 15 }}
          >
            <h2 className="mb-2 text-19px md:mb-0">{t("Categories")}</h2>

            <div className="flex w-full flex-wrap gap-2 justify-center md:justify-end">
            
                <AppOutlineButton
                  type="Default"
                  size="lg"
                  onClick={() => {
                    getGetMenuInfoByMenuId(params?.menuId || menuId);
                    updateState("isLinkMenuWithStoresModalOpen", true);
                  }}
                >
                  {t("Connections")}
                </AppOutlineButton>

                <AppOutlineButton
                  variant="secondary"
                  size="lg"
                  onClick={() => {
                    if (!menuState?.menuInfo?.isDefault) {
                      setIsMakeDefault(true);
                    }
                  }}
                >
                  {menuState?.menuInfo?.isDefault
                    ? t("Default Menu")
                    : t("Set Default Menu")}
                </AppOutlineButton>

                <AppButton
                  type="primary"
                  size="lg"
                  onClick={() => {
                    updateState("dialogContentId", DG_CONTENT_ID.ADD_CATEGORY);
                    updateState("open", true);
                  }}
                >
                  {t("New Category")}
                </AppButton>
            </div>
          </div>

          <Accordion>
            {isMobile ? (
              <>
                {menuState?.categories?.map((cat, index) =>
                  getCategoryView(cat.categoryId, index)
                )}
              </>
            ) : (
              <ReactSortable
                list={menuState?.categories}
                setList={(newState) => {
                  updateState("categories", newState);
                }}
                animation={300}
                onUpdate={(cat, sortable, evt) => {
                  reOrderCategory(cat, sortable, evt);
                }}
              >
                {menuState?.categories?.map((cat, index) =>
                  getCategoryView(cat.categoryId, index)
                )}
              </ReactSortable>
            )}
          </Accordion>
        </div>
        <aside className="w-full min-h-0 md:flex md:h-full md:max-h-full md:flex-col md:self-stretch">
          <OrderingPagePreview
            as={as}
            state={menuState}
            updateState={updateState}
            menuId={menuId || params?.menuId}
          />
        </aside>
      </div>
    </AppContainer>
  );
};
export default MenuEditor;
